<template>
  <Authenticated>
    <CogsNav />
    <div class="base-recipes-container">
      <section class="recipe-section">
        <div class="content-wrapper">
          <Recipe :current-recipe="currentRecipe" recipe-type="prep" @recipe-saved="onRecipeSaved" />
          <div class="recipe-list">
            <div class="search">
              <div class="search-results-header">
                <h2 v-if="foundRecipes.length == 1">{{ foundRecipes.length }} result</h2>
                <h2 v-else-if="foundRecipes.length > 1">{{ foundRecipes.length }} results</h2>
                <h2 v-else>All Base Recipes ({{ cogsBaseRecipes.length }})</h2>
              </div>
              <input type="search" placeholder="Search Base Recipes..." v-model="recipesQuery" />
              <div class="recipe-filters">
                <select v-model="categoryFilter">
                  <option value="">All Categories</option>
                  <option v-for="category in cogsRecipeCategories" :key="category.key[0]" :value="category.key[0]">
                    {{ category.key[0] }}
                  </option>
                </select>
                <select v-model="costingTypeFilter">
                  <option value="">All Costing Types</option>
                  <option value="uom">UOM Based</option>
                  <option value="servings">Servings Based</option>
                </select>
              </div>
              <ul v-if="displayRecipes.length > 0">
                <li v-for="recipe in displayRecipes" :key="recipe.id" :class="{ active: currentRecipe && currentRecipe.id === recipe.id }">
                  <div class="recipe-item">
                    <div class="recipe-info">
                      <h3 class="recipe-name">{{ recipe.value.escaped }}</h3>
                      <div class="recipe-pricing" v-if="recipe.value.price_per_uom">
                        <p class="price">{{ recipe.value.price_per_uom }}</p>
                        <p v-if="recipe.value.cost_per_serving" class="cost-per-serving">
                          Cost per serving: <CurrencyFormatter :value="recipe.value.cost_per_serving" :currency="companySettings.currency" />
                        </p>
                      </div>
                      <div class="recipe-composition">
                        <span v-if="recipe.value.ingredients && recipe.value.ingredients.length" class="ingredients-count">
                          {{ recipe.value.ingredients.length }} ingredient{{ recipe.value.ingredients.length !== 1 ? "s" : "" }}
                        </span>
                        <span v-if="recipe.value.recipes && recipe.value.recipes.length" class="subrecipes-count">
                          {{ recipe.value.recipes.length }} sub-recipe{{ recipe.value.recipes.length !== 1 ? "s" : "" }}
                        </span>
                      </div>
                    </div>
                    <div class="recipe-actions">
                      <button @click="loadRecipe(recipe)" class="edit-btn"><font-awesome-icon icon="edit" /> Edit</button>
                      <button @click="viewRecipeUsage(recipe)" class="usage-btn"><font-awesome-icon icon="link" /> Used ({{ getRecipeUsage(recipe).length }})</button>
                    </div>
                  </div>
                </li>
              </ul>
              <div v-else class="no-recipes">
                <p v-if="recipesQuery || categoryFilter || costingTypeFilter">No recipes match your current filters</p>
                <p v-else>No base recipes found. Create your first recipe using the form.</p>
              </div>
              <div v-if="usageRecipe" class="usage-panel">
                <p class="blue">Used in: {{ usageRecipe.value.escaped }}</p>
                <ul>
                  <li v-for="r in usageList" :key="r.id">{{ r.value.escaped }}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </Authenticated>
</template>

<script>
import Authenticated from "@/components/_layouts/Authenticated";
import Recipe from "./Recipe.vue";
import { cogsMixin } from "./shared/cogsMixin";
import { CurrencyFormatter } from "@monolith/components";
import CogsNav from "./shared/CogsNav.vue";

export default {
  name: "COGSBaseRecipes",
  components: { Authenticated, Recipe, CurrencyFormatter, CogsNav },
  mixins: [cogsMixin],
  data() {
    return {
      recipesQuery: "",
      foundRecipes: [],
      categoryFilter: "",
      costingTypeFilter: "",
      usageRecipe: null,
      usageList: [],
      currentRecipe: {
        value: {
          escaped: "",
          price_per_uom: "",
          name: "",
          type: "prep",
          ingredients: [],
          recipes: [],
        },
      },
    };
  },
  computed: {
    displayRecipes() {
      let recipes = this.foundRecipes.length > 0 ? this.foundRecipes : this.cogsBaseRecipes;

      if (this.categoryFilter) {
        recipes = recipes.filter((recipe) => recipe.value.name === this.categoryFilter);
      }

      if (this.costingTypeFilter) {
        recipes = recipes.filter((recipe) => recipe.value.costing_type === this.costingTypeFilter);
      }

      return recipes;
    },
  },
  methods: {
    loadRecipe(recipe) {
      this.currentRecipe = JSON.parse(JSON.stringify(recipe));
    },

    viewRecipeUsage(recipe) {
      const usage = this.getRecipeUsage(recipe);
      if (usage.length === 0) return;
      this.usageRecipe = this.usageRecipe && this.usageRecipe.id === recipe.id ? null : recipe;
      this.usageList = usage;
    },

    getRecipeUsage(recipe) {
      return [...this.cogsBaseRecipes, ...this.cogsMenuRecipes].filter((r) => r.value.recipes && r.value.recipes.some((subRecipe) => subRecipe.id === recipe.id));
    },

    onRecipeSaved() {
      this.currentRecipe = {
        value: {
          escaped: "",
          price_per_uom: "",
          name: "",
          type: "prep",
          ingredients: [],
          recipes: [],
        },
      };
      this.clearFilters();
    },

    clearFilters() {
      this.recipesQuery = "";
      this.categoryFilter = "";
      this.costingTypeFilter = "";
      this.foundRecipes = [];
    },

  },

  watch: {
    recipesQuery: function () {
      this.foundRecipes = this.queryCheck(this.recipesQuery, this.cogsSearchRecipes);
    },
  },

  mounted() {
    if (this.$route.params.loadProduct && this.$route.params.recipeId) {
      this.$nextTick(() => {
        const product = this.findRecipeByID(this.$route.params.recipeId);
        if (product) {
          this.loadRecipe(product);
        }
      });
    }
  },
};
</script>

<style lang="scss" scoped>
.base-recipes-container {
  padding: 0 1rem;

  .recipe-section {
    display: flex;
    flex-direction: column;
    margin: 1rem;

    h2 {
      text-transform: capitalize;
      margin: 0;
      padding: 1rem;
    }

    .content-wrapper {
      display: flex;
      flex-direction: row-reverse;
      justify-content: space-between;
      align-items: flex-start;

      gap: 1rem;

      .recipe-list {
        display: flex;
        flex: 1;

        .search {
          background-color: var(--primary-colour);
          box-shadow: var(--box-shadow);
          border-radius: 1rem;
          display: flex;
          flex: 1;
          flex-direction: column;
          align-items: flex-start;
          padding: 1.5rem;

          input {
            width: 100%;
            padding: 0.5rem;
            box-shadow: var(--box-shadow);
            border: 1px solid var(--secondary-colour);
            border-radius: 0.25rem;
            background-color: var(--opacity-background);
            color: var(--action-colour);
            margin: 0.5rem 0;
          }

          .search-results-header {
            width: 100%;
            margin-bottom: 0.5rem;

            h2 {
              margin: 0.5rem;
              font-size: 1.1rem;
              padding: 0;
            }
          }

          .recipe-filters {
            display: flex;
            gap: 0.5rem;
            width: 100%;
            margin-bottom: 1rem;

            select {
              flex: 1;
              padding: 0.5rem;
              border: 1px solid var(--secondary-colour);
              box-shadow: var(--box-shadow);
              border-radius: 3px;
            }
          }

          ul {
            height: 60vh;
            overflow: auto;
            margin: 0;
            width: 100%;
            padding: 0;

            li {
              margin: 0.5rem;

              .recipe-item {
                display: flex;

                justify-content: space-between;
                padding: 0.75rem;
                background-color: var(--opacity-background);
                border-radius: 0.5rem;
                box-shadow: var(--box-shadow);

                .recipe-info {
                  .recipe-name {
                    cursor: pointer;
                    margin: 0.5rem 0;
                    color: var(--action-colour);
                    font-size: 1.1rem;
                    font-weight: 600;
                  }

                  .recipe-pricing {
                    margin-bottom: 0.5rem;
                    display: flex;
                    flex-direction: column;
                    p {
                      margin: 0.15rem 0;
                      font-size: 0.9rem;
                      color: var(--text-colour);
                    }
                  }

                  .recipe-composition {
                    margin-bottom: 0.75rem;
                    font-size: 0.9rem;
                    color: var(--text-colour);

                    .ingredients-count,
                    .subrecipes-count {
                      margin-right: 1rem;
                    }
                  }
                }

                .recipe-actions {
                  display: flex;
                  flex-direction: column;
                  justify-content: center;
                  gap: 0.5rem;

                  button {
                    border: none;
                    border-radius: 0.5rem;
                    cursor: pointer;
                    width: 10rem;
                    height: 2rem;
                    padding: 0 !important;
                    font-size: 0.75rem;
                  }

                  .edit-btn {
                    background-color: var(--action-colour);
                    color: var(--secondary-text-colour);
                  }

                  .usage-btn {
                    background-color: var(--primary-colour);
                    color: var(--text-colour);
                  }
                }
              }
            }
          }

          .no-recipes {
            display: flex;
            justify-content: center;
            align-items: center;
            color: var(--text-colour);
            font-style: italic;
            width: 100%;
            padding: 2rem 0;
          }
        }
      }
    }
  }

}

@media only screen and (orientation: portrait) {
  .base-recipes-container {
    .recipe-section {
      .content-wrapper {
        flex-direction: column;

        .recipe-list {
          margin: 1rem auto;
          max-width: 95%;
          min-width: 95%;

          .search {
            min-width: 100%;

            .recipe-filters {
              flex-direction: column;
            }

            ul {
              height: 40vh;
            }
          }
        }
      }
    }

  }
}
</style>
